import { useState, useEffect } from 'react';
import { ApolloError } from '@apollo/client';
import { CheckIcon, DuplicateIcon } from '@heroicons/react/outline';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import {
  useCreateExternalPractitionerFormInviteMutation,
  useContactsForProgramQuery,
  useProviderAssessmentsWithoutScheduleQuery,
  PatientContact,
} from '../../../../generated/graphql';

import ModalDialog from '../../ModalDialog';
import Button from '../../Button';
import { customToast } from '../../ToastAlert/customToast';

function getContactDisplayName(contact: PatientContact): string {
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
  return contact.email ? `${name} (${contact.email})` : name;
}

type SendFormToExternalModalProps = {
  isOpen: boolean;
  setClosed: () => void;
  programId: string;
  patientFirstName?: string;
  preselectedContactId?: string;
};

export default function SendFormToExternalModal({
  isOpen,
  setClosed,
  programId,
  patientFirstName,
  preselectedContactId: initialPreselectedContactId = '',
}: SendFormToExternalModalProps) {
  const [selectedAssessmentId, setSelectedAssessmentId] = useState('');
  const [selectedContactId, setSelectedContactId] = useState('');
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState<string | null>(null);
  const [selectedContactDisplay, setSelectedContactDisplay] = useState<{
    name: string;
    email: string;
  } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (initialPreselectedContactId) {
      setSelectedContactId(initialPreselectedContactId);
    } else {
      setSelectedContactId('');
    }
  }, [initialPreselectedContactId, isOpen]);

  const { data: contactsData, loading: contactsLoading } =
    useContactsForProgramQuery({
      variables: { programId },
      skip: !programId,
      fetchPolicy: isOpen ? 'network-only' : 'cache-first',
    });

  const { data: providerAssessmentsData } =
    useProviderAssessmentsWithoutScheduleQuery();

  const contacts = contactsData?.contactsForProgram ?? [];

  const practitionerOnlyForms = (providerAssessmentsData?.forms ?? []).filter(
    (f) => f.completableByProviderOnly === true,
  );

  const [createInvite, { loading: createInviteLoading }] =
    useCreateExternalPractitionerFormInviteMutation();

  const handleSubmit = async () => {
    if (!selectedAssessmentId) {
      customToast.error('Please select a form that the Contact will complete.');
      return;
    }

    const selectedContact = contacts.find((c) => c.id === selectedContactId);
    if (!selectedContact) {
      customToast.error('Please select a Contact.');
      return;
    }

    try {
      const result = await createInvite({
        variables: {
          input: {
            programId,
            assessmentId: selectedAssessmentId,
            recipientContactId: selectedContactId,
          },
        },
      });

      const payload = result.data?.createExternalPractitionerFormInvite;
      if (payload?.url) {
        setInviteUrl(payload.url);
        setSelectedContactDisplay({
          name: [selectedContact.firstName, selectedContact.lastName]
            .filter(Boolean)
            .join(' '),
          email: selectedContact.email ?? '',
        });
        if (selectedContact.email) {
          setEmailSent(selectedContact.email);
        }
      }
    } catch (err) {
      const message =
        (err as ApolloError)?.graphQLErrors?.[0]?.message ||
        (err instanceof Error
          ? err.message
          : 'Failed to create invite. Please try again.');
      customToast.error(message);
    }
  };

  const handleCopyLink = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success('Link copied to clipboard');
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleClose = () => {
    setInviteUrl(null);
    setEmailSent(null);
    setSelectedContactDisplay(null);
    setLinkCopied(false);
    setSelectedAssessmentId('');
    setSelectedContactId('');
    setClosed();
  };

  const preselectedContact = contacts.find(
    (c) => c.id === initialPreselectedContactId,
  );
  const isCompactMode = Boolean(initialPreselectedContactId);

  if (inviteUrl) {
    return (
      <ModalDialog
        title={`Success! Your form has been sent to ${
          patientFirstName ? `${patientFirstName}'s contact ` : ''
        }${
          selectedContactDisplay?.name ||
          selectedContactDisplay?.email ||
          'the recipient'
        }`}
        isOpen={isOpen}
        setClosed={handleClose}
        hideFooter={false}
        rightFooter={<Button title="Done" onClick={handleClose} />}
      >
        <div className="space-y-4">
          {emailSent && (
            <p className="text-body text-neutral-200">
              An email with the link to complete the form was sent to{' '}
              {emailSent}.
            </p>
          )}
          <p className="text-body text-neutral-200">
            You can also share the link with{' '}
            {selectedContactDisplay?.name ?? 'the recipient'} yourself so they
            can complete the form.
          </p>
          <Button
            title="Copy link"
            theme="secondary"
            IconComponent={linkCopied ? CheckIcon : DuplicateIcon}
            iconPosition="left"
            iconClassName={linkCopied ? 'text-green-150' : undefined}
            onClick={handleCopyLink}
          />
        </div>
      </ModalDialog>
    );
  }

  return (
    <ModalDialog
      title={`Send a form to ${
        patientFirstName ? `${patientFirstName}'s contact` : 'a Contact'
      }`}
      isOpen={isOpen}
      setClosed={handleClose}
      rightFooter={
        <Button
          title="Send form"
          onClick={handleSubmit}
          disabled={
            !selectedAssessmentId ||
            !selectedContactId ||
            contactsLoading ||
            createInviteLoading
          }
        />
      }
      bodyClassName="flex flex-col gap-4"
    >
      <div>
        <label className="mb-2 block text-caption font-medium text-neutral-200">
          Select the form to be completed
        </label>
        <select
          value={selectedAssessmentId}
          onChange={(e) => setSelectedAssessmentId(e.target.value)}
          className="max-h-14 w-full rounded-md border-0 bg-neutral-50 p-4 text-body text-green-150 shadow-sm focus:border-green-50 focus:ring-2 focus:ring-secondary-50"
        >
          <option value="">Select a form</option>
          {practitionerOnlyForms.map((form) => (
            <option key={form.id} value={form.id}>
              {form.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        {isCompactMode && preselectedContact ? (
          <p className="text-caption text-neutral-200">
            Sending to:{' '}
            <span className="font-medium text-neutral-150">
              {getContactDisplayName(preselectedContact)}
            </span>
          </p>
        ) : (
          <>
            <label className="mb-2 block text-caption font-medium text-neutral-200">
              Select the contact that will complete the form
            </label>
            <p className="mt-1 text-caption text-neutral-200">
              We'll send an email notification with a link to complete the form.
            </p>
          </>
        )}
        {!isCompactMode && contacts.length === 0 ? (
          <p className="mt-3 text-caption text-neutral-200">
            No Contacts found.{' '}
            <Link
              to={`/clients/${programId}/account#contacts`}
              className="font-medium text-green-150 underline hover:text-green-125"
              onClick={() => setClosed()}
            >
              Add a Contact in Account → Contacts
            </Link>
          </p>
        ) : !isCompactMode ? (
          <select
            value={selectedContactId}
            onChange={(e) => setSelectedContactId(e.target.value)}
            className="mt-3 max-h-14 w-full rounded-md border-0 bg-neutral-50 p-4 text-body text-green-150 shadow-sm focus:border-green-50 focus:ring-2 focus:ring-secondary-50"
          >
            <option value="">Select a contact</option>
            {contacts.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {getContactDisplayName(contact)}
              </option>
            ))}
          </select>
        ) : null}
      </div>
    </ModalDialog>
  );
}
